import { Document, Font, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import React from "react";
import boldRoboto from "@/assets/fonts/Roboto/Roboto-Bold.ttf";
import regularRobot from "@/assets/fonts/Roboto/Roboto-Regular.ttf";
import { numberFormater } from "@/utils";
import PropTypes from "prop-types";
const defaultLogo = `${window.location.origin}/images/logo.png`;

Font.register({
  family: "roboto",
  fonts: [{ src: regularRobot }, { src: boldRoboto, fontWeight: "bold" }],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: "24px 36px",
    fontSize: 9,
    fontFamily: "roboto",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 15,
  },
  headerBusiness: {
    fontWeight: "bold",
    fontSize: 11,
  },
  title: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 4,
    marginTop: 10,
    paddingBottom: 3,
    borderBottom: "1px solid black",
  },
  pageNumber: {
    textAlign: "right",
    fontSize: 8.5,
    marginBottom: 6,
  },
  customerInfo: {
    marginBottom: 4,
  },
  customerFlex: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 2,
    fontSize: 8.5,
  },
  table: {
    width: "100%",
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderTopColor: "#000",
    borderLeftColor: "#000",
    marginTop: 8,
  },
  tableRowHeader: {
    flexDirection: "row",
    backgroundColor: "#e7e6e6",
  },
  tableHeaderCell: {
    width: "100%",
    padding: "4px 8px",
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderBottomColor: "#000",
    borderRightColor: "#000",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: 9,
  },
  tableRow: {
    flexDirection: "row",
  },
  colLabel: {
    width: "70%",
    padding: "3px 8px",
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderBottomColor: "#000",
    borderRightColor: "#000",
    textAlign: "left",
    fontSize: 8.5,
  },
  colValue: {
    width: "30%",
    padding: "3px 8px",
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderBottomColor: "#000",
    borderRightColor: "#000",
    textAlign: "right",
    fontSize: 8.5,
  },
  footer: {
    marginTop: "auto",
    paddingTop: 10,
    fontSize: 8,
    textAlign: "center",
  },
});

// Create Document Component
const BillingReportPDF = ({ reportValues }) => {
  const logo = reportValues?.firstBusiness?.logo || defaultLogo;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* header */}
        <View fixed>
          <View style={styles.header}>
            <Image src={logo} style={{ width: "30%" }} />
            <View>
              <Text style={styles.headerBusiness}> {reportValues?.business} </Text>
              <Text>This electricity billing report is generated from IVIS.</Text>
            </View>
          </View>
          <Text style={styles.title}>Monthly Electricity Usage Report</Text>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`}
            fixed
          />
        </View>

        {/* customer info */}
        <View style={styles.customerInfo}>
          <Text style={{ fontWeight: "bold", marginBottom: 10 }}>Customer Information </Text>
          {reportFields.map((field, ind) => (
            <View style={styles.customerFlex} key={ind}>
              <Text style={{ width: "40%" }}> {field?.label} </Text>
              <Text> : </Text>

              <Text style={{ flex: 1, wordBreak: "break-word" }}>{reportValues[field?.value] || ""}</Text>
            </View>
          ))}
        </View>

        {/* electricity uses table */}
        {(Boolean(reportValues?.reportType) || Boolean(reportValues?.tariffName)) && (
          <>
            {/* Table 1: Electricity Usage Breakdown */}
            <View style={styles.table}>
              <View style={styles.tableRowHeader}>
                <Text style={styles.tableHeaderCell}>Electricity Usage Breakdown</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.colLabel}>Total Electricity Usage</Text>
                <Text style={styles.colValue}>
                  {numberFormater(reportValues?.totalUsageKwh ?? reportValues?.totalElectricityValue ?? 0)} kWh
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.colLabel}>Maximum Demand Value</Text>
                <Text style={styles.colValue}>
                  {numberFormater(reportValues?.maximumDemandKw ?? reportValues?.demandTotalValue ?? 0)} kW
                </Text>
              </View>
            </View>

            {/* Table 2: Electricity Bill Cost Breakdown */}
            <View style={{ ...styles.table, marginTop: 20 }}>
              <View style={styles.tableRowHeader}>
                <Text style={styles.tableHeaderCell}>Electricity Bill Cost Breakdown</Text>
              </View>

              {reportValues?.energyCharge !== undefined || reportValues?.capacityCharge !== undefined ? (
                <>
                  <View style={styles.tableRow}>
                    <Text style={styles.colLabel}>Energy Charge (RM)</Text>
                    <Text style={styles.colValue}>
                      {numberFormater(reportValues?.energyCharge ?? reportValues?.totalElectricityPrice ?? 0)}
                    </Text>
                  </View>
                  <View style={styles.tableRow}>
                    <Text style={styles.colLabel}>Capacity Charge (RM)</Text>
                    <Text style={styles.colValue}>
                      {numberFormater(reportValues?.capacityCharge ?? 0)}
                    </Text>
                  </View>
                  <View style={styles.tableRow}>
                    <Text style={styles.colLabel}>Network Charge (RM)</Text>
                    <Text style={styles.colValue}>
                      {numberFormater(reportValues?.networkCharge ?? 0)}
                    </Text>
                  </View>
                  <View style={styles.tableRow}>
                    <Text style={styles.colLabel}>Retail Charge (RM)</Text>
                    <Text style={styles.colValue}>
                      {numberFormater(reportValues?.retailCharge ?? 0)}
                    </Text>
                  </View>
                  <View style={styles.tableRow}>
                    <Text style={styles.colLabel}>AFA Rate (RM)</Text>
                    <Text style={styles.colValue}>
                      {numberFormater(reportValues?.afaCharge ?? 0)}
                    </Text>
                  </View>
                  <View style={styles.tableRow}>
                    <Text style={styles.colLabel}>Energy Efficiency Incentive (RM)</Text>
                    <Text style={styles.colValue}>
                      {numberFormater(reportValues?.incentiveCharge ?? 0)}
                    </Text>
                  </View>
                  <View style={styles.tableRow}>
                    <Text style={styles.colLabel}>
                      Service Tax ({reportValues?.type || 0}%)
                    </Text>
                    <Text style={styles.colValue}>
                      {numberFormater(reportValues?.serviceTax ?? 0)}
                    </Text>
                  </View>
                  <View style={{ ...styles.tableRow, fontWeight: "bold" }}>
                    <Text style={{ ...styles.colLabel, fontWeight: "bold" }}>Total Charge (RM)</Text>
                    <Text style={{ ...styles.colValue, fontWeight: "bold" }}>
                      {numberFormater(reportValues?.totalCharge ?? 0)}
                    </Text>
                  </View>
                </>
              ) : (
                <>
                  {/* Legacy Fallback */}
                  {reportValues?.reportType === "type1" && (
                    <>
                      <View style={styles.tableRow}>
                        <Text style={styles.colLabel}>Peak Usage This Month</Text>
                        <Text style={styles.colValue}>
                          {numberFormater(reportValues?.peakHourElectricityPrice || 0)}
                        </Text>
                      </View>
                      <View style={styles.tableRow}>
                        <Text style={styles.colLabel}>Off Peak Usage This Month</Text>
                        <Text style={styles.colValue}>
                          {numberFormater(reportValues?.offPeakHourElectricityPrice || 0)}
                        </Text>
                      </View>
                      <View style={styles.tableRow}>
                        <Text style={styles.colLabel}>Maximum Demand (kW)</Text>
                        <Text style={styles.colValue}>
                          {numberFormater(reportValues?.demandTotalPrice || 0)}
                        </Text>
                      </View>
                    </>
                  )}
                  {reportValues?.reportType === "type2" && (
                    <>
                      <View style={styles.tableRow}>
                        <Text style={styles.colLabel}>All Usage This Month</Text>
                        <Text style={styles.colValue}>
                          {numberFormater(reportValues?.totalElectricityPrice || 0)}
                        </Text>
                      </View>
                      <View style={styles.tableRow}>
                        <Text style={styles.colLabel}>Maximum Demand (kW)</Text>
                        <Text style={styles.colValue}>
                          {numberFormater(reportValues?.demandTotalPrice || 0)}
                        </Text>
                      </View>
                    </>
                  )}
                  <View style={styles.tableRow}>
                    <Text style={styles.colLabel}>
                      Service Tax ({reportValues?.type || 0}%)
                    </Text>
                    <Text style={styles.colValue}>
                      {numberFormater(reportValues?.serviceTax || 0)}
                    </Text>
                  </View>
                  <View style={{ ...styles.tableRow, fontWeight: "bold" }}>
                    <Text style={{ ...styles.colLabel, fontWeight: "bold" }}>Total Charge (RM)</Text>
                    <Text style={{ ...styles.colValue, fontWeight: "bold" }}>
                      {numberFormater(reportValues?.totalCharge ?? 0)}
                    </Text>
                  </View>
                </>
              )}
            </View>
          </>
        )}

        {/* Footer section */}
        <View fixed style={styles.footer}>
          <Text>If there are any discrepancies or disputes, kindly contact your building management.</Text>
          <Text style={{ fontWeight: "bold", marginTop: 20 }}>
            THIS IS AN ELECTRONICALLY GENERATED REPORT FROM iViS
          </Text>
        </View>
      </Page>
    </Document>
  );
};

BillingReportPDF.propTypes = {
  reportValues: PropTypes.object.isRequired,
};

export default BillingReportPDF;

const reportFields = [
  { label: "Report Generation Date", value: "reportGenerationDate" },
  { label: "Monthly Cycle Selected", value: "monthlyCycleSelected" },
  { label: "Site Location", value: "site" },
  { label: "Device Name", value: "device" },
  { label: "Device ID", value: "device" },
  { label: "Installed Area", value: "installedArea" },
  { label: "Electricity Tariff Type", value: "tariffName" },
];
